import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { GRADE_LEVELS, COURSES, labelForGrade } from "@/lib/constants";
import { topicsFor } from "@/lib/topics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare, History, User, CreditCard, ArrowRight, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";

// Track C: filter legacy K-12 course list to Sayısal-only subjects at
// the point of use so a NEW Sayısal learner cannot select Turkish /
// history / geography / English inside the Sayısal product. Legacy
// stored values (`selected_course`) remain readable and are still
// honoured by the legacy chat backend.
const SAYISAL_ALLOWED_COURSES = new Set([
  "Matematik", "Fen Bilimleri", "Fizik", "Kimya", "Biyoloji",
]);

export default function Panel() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [usage, setUsage] = useState(null);
  const [grade, setGrade] = useState(user?.grade_level || "");
  const [course, setCourse] = useState(user?.selected_course || "");
  const [savedSignal, setSavedSignal] = useState(false);
  const savedGrade = user?.grade_level || "";
  const savedCourse = user?.selected_course || "";
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("claimed") === "true") {
      toast.success("Önceki tanılama oturumun hesabına bağlandı.", {
        id: "sayisal-claim-toast",
      });
      const next = new URLSearchParams(searchParams);
      next.delete("claimed");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.get("/subscription/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  // Auto-persist preferences whenever grade/course changes.
  useEffect(() => {
    // Skip when values haven't diverged from saved user data.
    if (grade === savedGrade && course === savedCourse) return;
    // Skip when either is empty (partial selection) — persist only complete state.
    if (!grade && !course) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        await api.patch("/auth/profile", {
          grade_level: grade || null,
          selected_course: course || null,
        });
        if (cancelled) return;
        await refresh();
        setSavedSignal(true);
        setTimeout(() => !cancelled && setSavedSignal(false), 1600);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [grade, course, savedGrade, savedCourse, refresh]);

  const openTopic = (prompt) => {
    if (!grade || !course) {
      toast.error("Önce sınıf ve ders seç");
      return;
    }
    const params = new URLSearchParams({ prompt, grade, course });
    nav(`/panel/sohbet?${params.toString()}`);
  };

  const canStartChat = Boolean(grade) && Boolean(course);
  const topics = course ? topicsFor(course) : [];

  return (
    <AppShell variant="student">
      <div className="max-w-5xl mx-auto p-5 lg:p-10 space-y-8" data-testid="panel-page">
        <div>
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Panel</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">
            Merhaba, {user?.name?.split(" ")[0]}. Sayısal öğrenmeye başlayalım.
          </h1>
          <p className="mt-2 text-zinc-600">
            Sayısal içerik motoru dersin için aktif olduğunda burada tanılama akışı görünecek.
            Hazırlığımız devam ediyor.
          </p>
        </div>

        <section className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-sm font-semibold text-zinc-900">Öğrenme tercihlerin</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="text-xs text-zinc-500">Sınıf seviyesi</div>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger data-testid="panel-grade-select" className="rounded-xl">
                  <SelectValue placeholder="Sınıfını seç" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_LEVELS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="text-xs text-zinc-500">Ders</div>
              <Select value={course} onValueChange={setCourse}>
                <SelectTrigger data-testid="panel-course-select" className="rounded-xl">
                  <SelectValue placeholder="Ders seç" />
                </SelectTrigger>
                <SelectContent>
                  {COURSES.filter((c) => SAYISAL_ALLOWED_COURSES.has(c) || c === savedCourse).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="text-xs text-zinc-500 flex items-center gap-2" data-testid="panel-autosave-status">
              {savedSignal ? (
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                  </svg>
                  Tercihlerin kaydedildi
                </span>
              ) : (
                canStartChat ? "Değişiklikler otomatik kaydedilir." : "Sınıf ve ders seç, sohbet açılsın."
              )}
            </div>
            {canStartChat ? (
              <Link to="/panel/sohbet" className="sm:ml-auto" data-testid="panel-start-chat">
                <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full sm:w-auto">
                  Sohbete Başla
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <Button
                disabled
                className="sm:ml-auto rounded-full bg-[#1E3A8A] text-white w-full sm:w-auto opacity-50 cursor-not-allowed"
                data-testid="panel-start-chat-disabled"
                title="Önce sınıf ve ders seç"
              >
                Sohbete Başla
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </section>

        {course && topics.length > 0 && (
          <section className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8" data-testid="panel-topics">
            <div className="flex items-center gap-2 mb-5">
              <Lightbulb className="w-4 h-4 text-[#1E3A8A]" />
              <div className="text-sm font-semibold text-zinc-900">Önerilen konular · {course}</div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {topics.map((t, i) => (
                <button
                  key={i}
                  onClick={() => openTopic(t)}
                  data-testid={`topic-${i}`}
                  className="text-left rounded-2xl border border-zinc-200 bg-zinc-50 hover:bg-white hover:border-zinc-300 p-4 transition-colors group"
                >
                  <div className="text-xs text-zinc-400 font-mono mb-2">0{i + 1}</div>
                  <div className="text-sm text-zinc-800 leading-relaxed">{t}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#1E3A8A] font-medium group-hover:gap-2 transition-all">
                    Sor <ArrowRight className="w-3 h-3" />
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="text-xs text-zinc-500">Planın</div>
            <div className="mt-1 font-display text-xl font-semibold text-zinc-900 capitalize">
              {user?.plan === "premium" ? "Premium" : "Ücretsiz"}
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              Günlük limit: {usage ? usage.daily_limit : "…"}
            </div>
            <Link to="/panel/abonelik" className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline">
              Detaylar <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="usage-card">
            <div className="text-xs text-zinc-500">Bugün kalan mesaj hakkın</div>
            {usage ? (
              <>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-bold text-[#1E3A8A]">
                    {usage.remaining_today}
                  </span>
                  <span className="text-sm text-zinc-500">/ {usage.daily_limit}</span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div
                    className="h-full bg-[#1E3A8A] transition-all"
                    style={{
                      width: `${
                        usage.daily_limit > 0
                          ? Math.max(0, Math.min(100, (usage.remaining_today / usage.daily_limit) * 100))
                          : 0
                      }%`,
                    }}
                    aria-hidden="true"
                  />
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  Bugün {usage.used_today} mesaj kullandın · Yarın sıfırlanır
                </div>
              </>
            ) : (
              <div className="mt-1 text-zinc-400">Yükleniyor…</div>
            )}
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="text-xs text-zinc-500">Aktif sınıf / ders</div>
            <div className="mt-1 font-display text-xl font-semibold text-zinc-900">
              {user?.grade_level ? labelForGrade(user.grade_level) : "—"}
            </div>
            <div className="text-sm text-zinc-500 mt-1">{user?.selected_course || "Ders seçilmedi"}</div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3">
          {[
            { to: "/panel/sohbet", icon: MessageSquare, label: "Yeni Sohbet" },
            { to: "/panel/gecmis", icon: History, label: "Geçmiş" },
            { to: "/panel/profil", icon: User, label: "Profil" },
            { to: "/panel/abonelik", icon: CreditCard, label: "Abonelik" },
          ].map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="rounded-2xl border border-zinc-200 bg-white p-4 hover:border-zinc-300 transition-colors flex items-center gap-3"
              data-testid={`quick-${q.label}`}
            >
              <div className="w-9 h-9 rounded-xl bg-zinc-100 flex items-center justify-center">
                <q.icon className="w-4 h-4 text-[#1E3A8A]" />
              </div>
              <div className="text-sm font-medium text-zinc-900">{q.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
